import PolygonIcon from '@apps-common/ui-icons/Polygon.jsx';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import React, {useMemo} from 'react';

import {useDispatch, useSelector} from 'react-redux';
import {deselectFeatureRef} from '@actions/selected-features';

import {trackEvent} from '@app/analytics';
import {useGetCollection} from '@app/api/collections';
import {useGetFeatureByReference} from '@app/api/features';
import AreaIndicator from '@app/components/common/AreaIndicator';
import CopyButton from '@app/components/common/CopyButton';
import LoadingMessage from '@app/components/common/LoadingMessage';
import {featureTitle} from '@app/util/features';
import {getCollectionIDFromReference} from '@app/util/granero';
import MoreActionsMenu from '@components/Features/FeatureActions/MoreActionsMenu';

const Details = () => {
  const dispatch = useDispatch();

  const selectedFeatureRef = useSelector(state => state.selectedFeatureRef);
  const {data: selectedFeature, isLoading: isFeatureLoading} =
    useGetFeatureByReference(
      {
        reference: selectedFeatureRef,
      },
      {
        enabled: !!selectedFeatureRef,
      }
    );
  const collectionId = useMemo(
    () => getCollectionIDFromReference(selectedFeatureRef),
    [selectedFeatureRef]
  );
  const {data: selectedCollection, isLoading: isCollectionLoading} =
    useGetCollection(
      {
        collectionId: collectionId,
      },
      {
        enabled: !!collectionId,
      }
    );

  const isOwner = selectedCollection?.permissions.is_owner;

  const handleClose = () => {
    dispatch(deselectFeatureRef());
  };

  if (!selectedFeatureRef) {
    return false;
  }

  const description =
    selectedFeature?.properties[selectedCollection?.description_property];

  return (
    <Card elevation={3} sx={{width: 300}}>
      <CardHeader
        title={<Typography role="title">Selection Details</Typography>}
        action={
          <>
            <MoreActionsMenu
              feature={selectedFeature}
              isOwner={isOwner}
              showViewDetails={false}
            />
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => handleClose()}
            >
              <CloseIcon />
            </IconButton>
          </>
        }
        sx={theme => ({
          paddingLeft: 1,
          paddingY: 1,
          borderBottom: 1,
          borderColor: 'divider',
          ...theme.applyStyles('light', {
            backgroundColor: 'grey.100',
          }),
        })}
      />
      <CardContent
        sx={{
          '&.MuiCardContent-root:last-child': {
            paddingBottom: 1,
          },
        }}
      >
        {isFeatureLoading || isCollectionLoading ? (
          <LoadingMessage
            progressSize={36}
            titleTypographyProps={{variant: 'h6'}}
          />
        ) : (
          <>
            <Stack>
              <Stack
                direction="row"
                sx={{
                  gap: 1,
                  alignItems: 'center',
                  marginBottom: 1,
                }}
              >
                <PolygonIcon fontSize="small" color="action" />
                <Typography data-testid="featureTitle" variant="subtitle1">
                  {featureTitle({
                    feature: selectedFeature,
                    collection: selectedCollection,
                  }) || <i>Untitled feature</i>}
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{color: 'text.secondary'}}>
                <AreaIndicator
                  squareMeters={selectedFeature.properties['pl:area']}
                />
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color={description ? 'text.primary' : 'text.secondary'}
            >
              {description || <i>no description</i>}
            </Typography>
            {selectedFeatureRef && (
              <>
                <Typography variant="subtitle1" sx={{marginY: 1}}>
                  Feature reference
                </Typography>
                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                  <Paper
                    elevation={24}
                    sx={theme => ({
                      ...theme.typography.caption,
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 'none',
                      paddingX: 0.5,
                      paddingY: 1,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      ...theme.applyStyles('light', {
                        backgroundColor: 'grey.100',
                      }),
                    })}
                  >
                    {selectedFeatureRef}
                  </Paper>

                  <CopyButton
                    onCopy={() =>
                      trackEvent('Copied feature reference in details panel', {
                        featureRef: selectedFeatureRef,
                      })
                    }
                    size="small"
                    textToCopy={selectedFeatureRef}
                    title="feature reference"
                  />
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default Details;
